import App from 'App';
import React, { Suspense } from 'react';
import { Route, createBrowserRouter, createRoutesFromElements, Navigate, redirect, useParams } from 'react-router-dom';
import { DashboardEditSessionProvider } from 'common/contexts/DashboardContext';
import { MAIN_ROUTES, SEGMENTS_ROUTES, SETTINGS_ROUTES } from 'settings/routes';
import Invite from 'views/Invite';
import Login from 'views/Login';
import NewPassword from 'views/NewPassword';
import ProfilePage from 'modules/settings/components/ProfilePage';
import WaveReports from 'modules/settings/components/WaveReports';
import Appearance from 'modules/settings/components/Appearance';
import Members from 'modules/settings/components/Members';
import DataExport from 'modules/settings/components/DataExport';
import AuthHandle from 'common/components/AuthHandle';
import LatanaSegmentsTab from 'modules/segments/components/LatanaSegmentsTab';
import CustomSegmentsTab from 'modules/segments/components/CustomSegmentsTab';
import { LoadingContainer } from 'common/styledComponents/containers';
import Spinner from 'common/components/Spinner';

const Charts = React.lazy(() => import('views/Charts'));
const ChatBot = React.lazy(() => import('views/ChatBot'));
const Settings = React.lazy(() => import('views/Settings'));
const Segments = React.lazy(() => import('views/Segments'));
const Dashboard = React.lazy(() => import('views/Dashboard'));
const Dashboards = React.lazy(() => import('views/Dashboards'));
const ChartViewLoader = React.lazy(() => import('views/ChartView/loader'));
const KpiCatalogue = React.lazy(() => import('views/KpiCatalogue'));
const ChatContent = React.lazy(() => import('modules/chatbot/components/ChatContent'));

export const Fallback: React.FC = () => (
  <LoadingContainer>
    <Spinner />
  </LoadingContainer>
);

const DashboardRoute: React.FC = () => {
  const { dashboardId = '' } = useParams();

  return (
    <DashboardEditSessionProvider key={dashboardId}>
      <Suspense fallback={<Fallback />}>
        <Dashboard />
      </Suspense>
    </DashboardEditSessionProvider>
  );
};

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<App />}>
      <Route path={MAIN_ROUTES.ROOT} element={<AuthHandle />}>
        <Route index element={<Navigate to={MAIN_ROUTES.DASHBOARDS} />} />
        <Route
          index
          path={MAIN_ROUTES.DASHBOARDS}
          element={
            <Suspense fallback={<Fallback />}>
              <Dashboards />
            </Suspense>
          }
        />
        <Route
          path={MAIN_ROUTES.CHARTS}
          element={
            <Suspense fallback={<Fallback />}>
              <Charts />
            </Suspense>
          }
        />
        <Route
          path={MAIN_ROUTES.CHART_VIEW}
          element={
            <Suspense fallback={<Fallback />}>
              <ChartViewLoader />
            </Suspense>
          }
        />
        <Route
          path={MAIN_ROUTES.CHART_CREATE}
          element={
            <Suspense fallback={<Fallback />}>
              <ChartViewLoader />
            </Suspense>
          }
        />

        <Route path={MAIN_ROUTES.DASHBOARD} element={<DashboardRoute />} />
        <Route
          path={MAIN_ROUTES.CHAT_BOT}
          element={
            <Suspense fallback={<Fallback />}>
              <ChatBot />
            </Suspense>
          }
        >
          <Route index element={<Navigate to={MAIN_ROUTES.CHAT_NEW} replace />} />
          <Route
            path={MAIN_ROUTES.CHAT_NEW}
            element={
              <Suspense fallback={<Fallback />}>
                <ChatContent />
              </Suspense>
            }
          />
          <Route
            path={MAIN_ROUTES.CHAT_VIEW}
            element={
              <Suspense fallback={<Fallback />}>
                <ChatContent />
              </Suspense>
            }
          />
        </Route>
        <Route
          path={MAIN_ROUTES.SETTINGS}
          element={
            <Suspense fallback={<Fallback />}>
              <Settings />
            </Suspense>
          }
        >
          <Route path={SETTINGS_ROUTES.PROFILE} element={<ProfilePage />} />
          <Route path={SETTINGS_ROUTES.WAVE_REPORTS} element={<WaveReports />} />
          <Route path={SETTINGS_ROUTES.APPEARANCE} element={<Appearance />} />
          <Route path={SETTINGS_ROUTES.MEMBERS} element={<Members />} />
          <Route path={SETTINGS_ROUTES.DATA_EXPORT} element={<DataExport />} />
        </Route>
        <Route
          path={MAIN_ROUTES.SEGMENTS}
          element={
            <Suspense fallback={<Fallback />}>
              <Segments />
            </Suspense>
          }
        >
          <Route index element={<LatanaSegmentsTab />} />
          <Route path={SEGMENTS_ROUTES.LATANA_WITH_PARAM} element={<LatanaSegmentsTab />} />
          <Route path={SEGMENTS_ROUTES.CUSTOM_WITH_PARAM} element={<CustomSegmentsTab />} />
        </Route>
        <Route
          path={MAIN_ROUTES.KPI_CATALOGUE}
          element={
            <Suspense fallback={<Fallback />}>
              <KpiCatalogue />
            </Suspense>
          }
        />
      </Route>
      {/* Redirect old chart endpoint with study to the new one */}
      <Route
        path={MAIN_ROUTES.OLD_CHART_VIEW}
        loader={({ params }) => {
          const { chartId } = params;
          return redirect(`/chart/${chartId}`);
        }}
      />

      <Route path={MAIN_ROUTES.NEW_PASSWORD} element={<NewPassword />} />
      <Route path={MAIN_ROUTES.INVITE} element={<Invite />} />
      <Route index path={MAIN_ROUTES.LOGIN} element={<Login />} />
      <Route path={MAIN_ROUTES.INTERNAL_LOGIN} element={<Navigate to={MAIN_ROUTES.ROOT} replace />} />
    </Route>,
  ),
);
